import React, { useState } from 'react';
import { submitClaimWithDocuments, parseDocument } from '../services/api';

const DOCUMENT_TYPES = [
  'PRE_AUTH_FORM', 'DISCHARGE_SUMMARY', 'BILL', 'PRESCRIPTION',
  'LAB_REPORT', 'ID_PROOF', 'MEDICAL_CERTIFICATE', 'OTHER',
];
const ROOM_CATEGORIES = ['GENERAL', 'TWIN_SHARING', 'SINGLE_AC', 'SEMI_PRIVATE', 'PRIVATE', 'ICU'];
const GENDERS = ['M', 'F', 'O'];
const RELATIONSHIPS = ['Self', 'Spouse', 'Child', 'Parent'];
const POLICY_TYPES = ['INDIVIDUAL', 'FAMILY_FLOATER', 'GROUP'];
const ADMISSION_NATURES = ['EMERGENCY', 'PLANNED', 'DAY_CARE', 'MATERNITY'];
const LINE_OF_TREATMENTS = ['MEDICAL', 'SURGICAL', 'DAY_CARE'];

const emptyProcedure = { cpt_code: '', icd10_pcs_code: null, description: '', amount: '', date: '' };
const emptyDiagnosis = { code: '', description: '' };
const emptyCost = {
  room_rent_per_day: '', consultation_charges: '', investigation_charges: '',
  surgery_charges: '', icu_charges: '', medicine_consumables: '',
  implant_cost: '', other_charges: '',
};

export default function ClaimIntakeForm({ onSubmitSuccess }) {
  const [form, setForm] = useState({
    patient_name: '', patient_gender: 'M', patient_age: '', patient_dob: '',
    patient_relationship: '', policy_holder_name: '',
    patient_id: '', policy_id: '', health_card_id: '', employee_id: '',
    patient_contact: '', patient_email: '',
    insurance_company: '', tpa_name: '', policy_type: '',
    policy_start_date: '', policy_end_date: '',
    sum_insured: '', available_balance: '',
    hospital_id: '', hospital_name: '', hospital_address: '',
    hospital_city: '', hospital_pincode: '', hospital_contact: '',
    hospital_email: '', hospital_provider_id: '',
    treating_doctor_name: '', doctor_registration_no: '',
    nature_of_admission: '', admission_date: '', admission_time: '',
    discharge_date: '', expected_days_of_stay: '', room_category: '',
    primary_diagnosis: { code: '', description: '' },
    secondary_diagnoses: [],
    symptoms_and_findings: '', past_medical_history: '',
    comorbid_conditions: '', duration_of_illness: '',
    line_of_treatment: '',
    procedures: [{ ...emptyProcedure }],
    investigation_planned: '', anesthesia_type: '',
    cost_breakdown: { ...emptyCost },
    total_claimed_amount: '',
    doctor_declaration: false, patient_declaration: false,
    claim_type: 'CASHLESS',
  });

  const [files, setFiles] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const u = (field, value) => setForm(p => ({ ...p, [field]: value }));
  const uDiag = (field, value) => setForm(p => ({ ...p, primary_diagnosis: { ...p.primary_diagnosis, [field]: value } }));
  const uCost = (field, value) => setForm(p => ({ ...p, cost_breakdown: { ...p.cost_breakdown, [field]: value } }));

  const addProcedure = () => setForm(p => ({ ...p, procedures: [...p.procedures, { ...emptyProcedure }] }));
  const addProcedureOfType = (type) => setForm(p => ({
    ...p,
    procedures: [...p.procedures, {
      cpt_code: type === 'cpt' ? '' : null,
      icd10_pcs_code: type === 'pcs' ? '' : null,
      description: '', amount: '', date: '',
    }],
  }));
  const updateProcedure = (i, field, value) => setForm(p => {
    const updated = [...p.procedures];
    updated[i] = { ...updated[i], [field]: field === 'amount' ? (value === '' ? '' : Number(value)) : value };
    return { ...p, procedures: updated };
  });
  const removeProcedure = (i) => setForm(p => ({ ...p, procedures: p.procedures.filter((_, idx) => idx !== i) }));

  const addSecDiag = () => setForm(p => ({ ...p, secondary_diagnoses: [...p.secondary_diagnoses, { ...emptyDiagnosis }] }));
  const updateSecDiag = (i, field, value) => setForm(p => {
    const updated = [...p.secondary_diagnoses];
    updated[i] = { ...updated[i], [field]: value };
    return { ...p, secondary_diagnoses: updated };
  });
  const removeSecDiag = (i) => setForm(p => ({ ...p, secondary_diagnoses: p.secondary_diagnoses.filter((_, idx) => idx !== i) }));

  const handleFileAdd = (e) => {
    const newFiles = Array.from(e.target.files);
    setFiles(p => [...p, ...newFiles]);
    setDocumentTypes(p => [...p, ...newFiles.map(() => 'PRE_AUTH_FORM')]);
    e.target.value = '';
  };
  const updateDocType = (i, v) => setDocumentTypes(p => { const c = [...p]; c[i] = v; return c; });
  const removeFile = (i) => { setFiles(p => p.filter((_, idx) => idx !== i)); setDocumentTypes(p => p.filter((_, idx) => idx !== i)); };

  const handleParseDocument = async (file) => {
    setParsing(true);
    setError(null);
    try {
      const parsed = await parseDocument(file);
      const sf = parsed.structured_fields || {};
      setForm(p => ({
        ...p,
        patient_name: sf.patient_name || p.patient_name,
        patient_gender: sf.patient_gender || p.patient_gender,
        patient_age: sf.patient_age || p.patient_age,
        patient_dob: sf.patient_dob || p.patient_dob,
        patient_relationship: sf.patient_relationship || p.patient_relationship,
        policy_holder_name: sf.policy_holder_name || p.policy_holder_name,
        patient_id: sf.patient_id || p.patient_id,
        policy_id: sf.policy_id || p.policy_id,
        health_card_id: sf.health_card_id || p.health_card_id,
        employee_id: sf.employee_id || p.employee_id,
        patient_contact: sf.patient_contact || p.patient_contact,
        patient_email: sf.patient_email || p.patient_email,
        insurance_company: sf.insurance_company || p.insurance_company,
        tpa_name: sf.tpa_name || p.tpa_name,
        policy_type: sf.policy_type || p.policy_type,
        policy_start_date: sf.policy_start_date || p.policy_start_date,
        policy_end_date: sf.policy_end_date || p.policy_end_date,
        sum_insured: sf.sum_insured || p.sum_insured,
        available_balance: sf.available_balance || p.available_balance,
        hospital_id: sf.hospital_id || p.hospital_id,
        hospital_name: sf.hospital_name || p.hospital_name,
        hospital_address: sf.hospital_address || p.hospital_address,
        hospital_city: sf.hospital_city || p.hospital_city,
        hospital_pincode: sf.hospital_pincode || p.hospital_pincode,
        hospital_contact: sf.hospital_contact || p.hospital_contact,
        hospital_email: sf.hospital_email || p.hospital_email,
        hospital_provider_id: sf.hospital_provider_id || p.hospital_provider_id,
        treating_doctor_name: sf.treating_doctor_name || p.treating_doctor_name,
        doctor_registration_no: sf.doctor_registration_no || p.doctor_registration_no,
        nature_of_admission: sf.nature_of_admission || p.nature_of_admission,
        admission_date: sf.admission_date || p.admission_date,
        admission_time: sf.admission_time || p.admission_time,
        discharge_date: sf.discharge_date || p.discharge_date,
        expected_days_of_stay: sf.expected_days_of_stay || p.expected_days_of_stay,
        room_category: sf.room_category || p.room_category,
        primary_diagnosis: {
          code: sf.diagnosis_code || p.primary_diagnosis.code,
          description: sf.diagnosis_description || p.primary_diagnosis.description,
        },
        procedures: (Array.isArray(sf.procedures) && sf.procedures.length > 0)
          ? sf.procedures.map(proc => ({
              cpt_code: proc.cpt_code ? proc.cpt_code : (proc.icd10_pcs_code ? null : ''),
              icd10_pcs_code: proc.icd10_pcs_code ? proc.icd10_pcs_code : (proc.cpt_code ? null : ''),
              description: proc.description || '',
              amount: proc.amount || '',
              date: proc.date || '',
            }))
          : p.procedures,
        symptoms_and_findings: sf.symptoms_and_findings || p.symptoms_and_findings,
        past_medical_history: sf.past_medical_history || p.past_medical_history,
        comorbid_conditions: sf.comorbid_conditions || p.comorbid_conditions,
        duration_of_illness: sf.duration_of_illness || p.duration_of_illness,
        line_of_treatment: sf.line_of_treatment || p.line_of_treatment,
        investigation_planned: sf.investigation_planned || p.investigation_planned,
        anesthesia_type: sf.anesthesia_type || p.anesthesia_type,
        cost_breakdown: {
          room_rent_per_day: sf.room_rent_per_day || p.cost_breakdown.room_rent_per_day,
          consultation_charges: sf.consultation_charges || p.cost_breakdown.consultation_charges,
          investigation_charges: sf.investigation_charges || p.cost_breakdown.investigation_charges,
          surgery_charges: sf.surgery_charges || p.cost_breakdown.surgery_charges,
          icu_charges: sf.icu_charges || p.cost_breakdown.icu_charges,
          medicine_consumables: sf.medicine_consumables || p.cost_breakdown.medicine_consumables,
          implant_cost: sf.implant_cost || p.cost_breakdown.implant_cost,
          other_charges: sf.other_charges || p.cost_breakdown.other_charges,
        },
        total_claimed_amount: sf.total_claimed_amount || p.total_claimed_amount,
      }));
    } catch (err) {
      setError('Failed to parse document: ' + (err.response?.data?.detail || err.message));
    } finally {
      setParsing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(null); setResult(null);
    try {
      const cb = form.cost_breakdown;
      const payload = {
        ...form,
        patient_age: Number(form.patient_age) || 0,
        total_claimed_amount: Number(form.total_claimed_amount) || 0,
        sum_insured: form.sum_insured ? Number(form.sum_insured) : null,
        available_balance: form.available_balance ? Number(form.available_balance) : null,
        expected_days_of_stay: form.expected_days_of_stay ? Number(form.expected_days_of_stay) : null,
        procedures: form.procedures.map(p => ({ ...p, amount: Number(p.amount) || 0 })),
        room_category: form.room_category || null,
        policy_type: form.policy_type || null,
        nature_of_admission: form.nature_of_admission || null,
        line_of_treatment: form.line_of_treatment || null,
        cost_breakdown: {
          room_rent_per_day: Number(cb.room_rent_per_day) || 0,
          consultation_charges: Number(cb.consultation_charges) || 0,
          investigation_charges: Number(cb.investigation_charges) || 0,
          surgery_charges: Number(cb.surgery_charges) || 0,
          icu_charges: Number(cb.icu_charges) || 0,
          medicine_consumables: Number(cb.medicine_consumables) || 0,
          implant_cost: Number(cb.implant_cost) || 0,
          other_charges: Number(cb.other_charges) || 0,
        },
      };
      const response = await submitClaimWithDocuments(payload, files, documentTypes);
      setResult(response);
      if (onSubmitSuccess) onSubmitSuccess(response);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'object') {
        const errorMsg = detail.error || '';
        const validation = detail.validation || {};
        if (errorMsg.toLowerCase().includes('duplicate')) {
          setError(`⚠️ Duplicate Claim Detected\n\nA claim with the same Patient ID, Admission Date, and Hospital ID already exists.\n\n${errorMsg}\n\nPlease check the Dashboard for the existing claim.`);
        } else if (validation.format_errors && validation.format_errors.length > 0) {
          setError(`❌ Validation Failed\n\n${validation.format_errors.map(e => '• ' + e).join('\n')}${validation.warnings?.length ? '\n\nWarnings:\n' + validation.warnings.map(w => '• ' + w).join('\n') : ''}`);
        } else if (errorMsg) {
          setError(`❌ ${errorMsg}`);
        } else {
          setError(`❌ Claim submission failed. Please check the form and try again.`);
        }
      } else {
        setError(detail || err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const F = ({ label, required, children }) => (
    <div style={s.field}><label>{label}{required && ' *'}</label>{children}</div>
  );
  const Inp = ({ field, required, type = 'text', ...props }) => (
    <input required={required} type={type} value={form[field]} onChange={e => u(field, e.target.value)} style={s.input} autoComplete="off" spellCheck="false" {...props} />
  );
  const Sel = ({ field, options, required }) => (
    <select required={required} value={form[field]} onChange={e => u(field, e.target.value)} style={s.input}>
      <option value="">Select...</option>
      {options.map(o => <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>)}
    </select>
  );

  return (
    <div style={s.container}>
      <h2 style={s.title}>Cashless Claim — Pre-Authorization Form</h2>

      {error && <div style={s.error}>{error}</div>}
      {result && (
        <div style={s.success}>
          <strong>Claim Created!</strong> ID: {result.claim_id} | Status: {result.status} | Documents: {result.documents_attached}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Document Upload */}
        <fieldset style={s.fieldset}>
          <legend style={s.legend}>Upload & Parse Documents (Optional)</legend>
          <p style={s.hint}>Upload a pre-auth form PDF to auto-fill fields using AI extraction.</p>
          <input type="file" accept=".pdf,.png,.jpg,.jpeg,.tiff" onChange={handleFileAdd} multiple />
          {files.map((file, i) => (
            <div key={i} style={s.fileRow}>
              <span style={s.fileName}>{file.name}</span>
              <select value={documentTypes[i]} onChange={e => updateDocType(i, e.target.value)} style={s.select}>
                {DOCUMENT_TYPES.map(dt => <option key={dt} value={dt}>{dt.replace(/_/g, ' ')}</option>)}
              </select>
              <button type="button" onClick={() => handleParseDocument(file)} disabled={parsing} style={s.btnSmall}>
                {parsing ? 'Validating...' : 'Validate'}
              </button>
              <button type="button" onClick={() => removeFile(i)} style={s.btnDanger}>✕</button>
            </div>
          ))}
        </fieldset>

        {/* Section 1: Patient / Insured Details */}
        <fieldset style={s.fieldset}>
          <legend style={s.legend}>1. Patient / Insured Details</legend>
          <div style={s.grid}>
            <F label="Patient Name" required><Inp field="patient_name" required /></F>
            <F label="Gender" required><Sel field="patient_gender" options={GENDERS} required /></F>
            <F label="Age" required><Inp field="patient_age" required type="number" min="0" max="150" /></F>
            <F label="Date of Birth"><Inp field="patient_dob" type="date" /></F>
            <F label="Relationship to Proposer"><Sel field="patient_relationship" options={RELATIONSHIPS} /></F>
            <F label="Policy Holder Name"><Inp field="policy_holder_name" /></F>
            <F label="Patient ID" required><Inp field="patient_id" required /></F>
            <F label="Health Card / UHID"><Inp field="health_card_id" /></F>
            <F label="Employee / Corporate ID"><Inp field="employee_id" /></F>
            <F label="Contact Number"><Inp field="patient_contact" /></F>
            <F label="Email"><Inp field="patient_email" type="email" /></F>
          </div>
        </fieldset>

        {/* Section 2: Insurance Details */}
        <fieldset style={s.fieldset}>
          <legend style={s.legend}>2. Insurance Details</legend>
          <div style={s.grid}>
            <F label="Policy Number" required><Inp field="policy_id" required /></F>
            <F label="Insurance Company"><Inp field="insurance_company" /></F>
            <F label="TPA Name"><Inp field="tpa_name" /></F>
            <F label="Policy Type"><Sel field="policy_type" options={POLICY_TYPES} /></F>
            <F label="Policy Start Date"><Inp field="policy_start_date" type="date" /></F>
            <F label="Policy End Date"><Inp field="policy_end_date" type="date" /></F>
            <F label="Sum Insured"><Inp field="sum_insured" type="number" min="0" /></F>
            <F label="Available Balance"><Inp field="available_balance" type="number" min="0" /></F>
          </div>
        </fieldset>

        {/* Section 3: Hospital Details */}
        <fieldset style={s.fieldset}>
          <legend style={s.legend}>3. Hospital Details</legend>
          <div style={s.grid}>
            <F label="Hospital ID" required><Inp field="hospital_id" required /></F>
            <F label="Hospital Name"><Inp field="hospital_name" /></F>
            <F label="Hospital Address"><Inp field="hospital_address" /></F>
            <F label="City"><Inp field="hospital_city" /></F>
            <F label="Pin Code"><Inp field="hospital_pincode" /></F>
            <F label="Hospital Contact"><Inp field="hospital_contact" /></F>
            <F label="Hospital Email"><Inp field="hospital_email" type="email" /></F>
            <F label="Provider / Empanelment ID"><Inp field="hospital_provider_id" /></F>
            <F label="Treating Doctor Name"><Inp field="treating_doctor_name" /></F>
            <F label="Doctor Registration No."><Inp field="doctor_registration_no" /></F>
          </div>
        </fieldset>

        {/* Section 4: Admission & Treatment Details */}
        <fieldset style={s.fieldset}>
          <legend style={s.legend}>4. Admission & Treatment Details</legend>
          <div style={s.grid}>
            <F label="Nature of Admission"><Sel field="nature_of_admission" options={ADMISSION_NATURES} /></F>
            <F label="Admission Date" required><Inp field="admission_date" required type="date" /></F>
            <F label="Admission Time"><Inp field="admission_time" type="time" /></F>
            <F label="Discharge Date"><Inp field="discharge_date" type="date" /></F>
            <F label="Expected Days of Stay"><Inp field="expected_days_of_stay" type="number" min="0" /></F>
            <F label="Room Category"><Sel field="room_category" options={ROOM_CATEGORIES} /></F>
          </div>
        </fieldset>

        {/* Section 5: Diagnosis & Clinical Details */}
        <fieldset style={s.fieldset}>
          <legend style={s.legend}>5. Diagnosis & Clinical Details</legend>
          <div style={s.grid}>
            <F label="ICD-10 Code" required>
              <input required value={form.primary_diagnosis.code} onChange={e => uDiag('code', e.target.value)} placeholder="e.g. I21.4" style={s.input} />
            </F>
            <F label="Diagnosis Description" required>
              <input required value={form.primary_diagnosis.description} onChange={e => uDiag('description', e.target.value)} placeholder="e.g. Acute Myocardial Infarction" style={s.input} />
            </F>
          </div>
          {/* Secondary Diagnoses */}
          <div style={{ marginTop: 12 }}>
            <label style={{ fontWeight: 600, fontSize: 13 }}>Secondary Diagnoses</label>
            {form.secondary_diagnoses.map((d, i) => (
              <div key={i} style={s.row}>
                <input value={d.code} onChange={e => updateSecDiag(i, 'code', e.target.value)} placeholder="ICD-10 Code" style={s.inputSmall} />
                <input value={d.description} onChange={e => updateSecDiag(i, 'description', e.target.value)} placeholder="Description" style={{ ...s.inputSmall, flex: 2 }} />
                <button type="button" onClick={() => removeSecDiag(i)} style={s.btnDanger}>✕</button>
              </div>
            ))}
            <button type="button" onClick={addSecDiag} style={s.btnAdd}>+ Add Secondary Diagnosis</button>
          </div>
          <div style={{ ...s.grid, marginTop: 12 }}>
            <F label="Symptoms & Clinical Findings">
              <textarea value={form.symptoms_and_findings} onChange={e => u('symptoms_and_findings', e.target.value)} style={s.textarea} />
            </F>
            <F label="Past Medical History">
              <textarea value={form.past_medical_history} onChange={e => u('past_medical_history', e.target.value)} style={s.textarea} />
            </F>
            <F label="Co-morbid Conditions"><Inp field="comorbid_conditions" /></F>
            <F label="Duration of Present Illness"><Inp field="duration_of_illness" /></F>
          </div>
        </fieldset>

        {/* Section 6: Proposed Treatment / Procedures */}
        <fieldset style={s.fieldset}>
          <legend style={s.legend}>6. Proposed Treatment / Procedures</legend>
          <div style={s.grid}>
            <F label="Line of Treatment"><Sel field="line_of_treatment" options={LINE_OF_TREATMENTS} /></F>
            <F label="Investigation Planned"><Inp field="investigation_planned" /></F>
            <F label="Anesthesia Type"><Inp field="anesthesia_type" /></F>
          </div>
          <div style={{ marginTop: 12 }}>
            {/* CPT Procedures */}
            <label style={{ fontWeight: 600, fontSize: 13 }}>CPT Procedures</label>
            <div style={{ ...s.row, marginBottom: 4, marginTop: 8 }}>
              <span style={s.colHeader}>CPT Code</span>
              <span style={{ ...s.colHeader, flex: 2 }}>Description</span>
              <span style={s.colHeader}>Amount</span>
              <span style={s.colHeader}>Date</span>
              <span style={{ width: 28 }}></span>
            </div>
            {form.procedures.filter(p => !(p.icd10_pcs_code !== null && p.icd10_pcs_code !== undefined && p.cpt_code === null)).length === 0 && (
              <p style={s.hint}>No CPT procedures added.</p>
            )}
            {form.procedures.map((proc, i) => {
              if (proc.icd10_pcs_code !== null && proc.icd10_pcs_code !== undefined && proc.cpt_code === null) return null;
              return (
                <div key={`cpt-${i}`} style={s.row}>
                  <input value={proc.cpt_code} onChange={e => updateProcedure(i, 'cpt_code', e.target.value)} placeholder="e.g. 47562" style={s.inputSmall} />
                  <input required value={proc.description} onChange={e => updateProcedure(i, 'description', e.target.value)} placeholder="Procedure name" style={{ ...s.inputSmall, flex: 2 }} />
                  <input required type="number" min="0" value={proc.amount} onChange={e => updateProcedure(i, 'amount', e.target.value)} placeholder="0" style={s.inputSmall} />
                  <input type="date" value={proc.date || ''} onChange={e => updateProcedure(i, 'date', e.target.value)} style={s.inputSmall} />
                  {form.procedures.length > 1 && <button type="button" onClick={() => removeProcedure(i)} style={s.btnDanger}>✕</button>}
                </div>
              );
            })}
            <button type="button" onClick={() => addProcedureOfType('cpt')} style={s.btnAdd}>+ Add CPT Procedure</button>

            {/* ICD-10 PCS Procedures */}
            <label style={{ fontWeight: 600, fontSize: 13, marginTop: 20, display: 'block' }}>ICD-10 PCS Procedures</label>
            <div style={{ ...s.row, marginBottom: 4, marginTop: 8 }}>
              <span style={s.colHeader}>ICD-10 PCS Code</span>
              <span style={{ ...s.colHeader, flex: 2 }}>Description</span>
              <span style={s.colHeader}>Amount</span>
              <span style={s.colHeader}>Date</span>
              <span style={{ width: 28 }}></span>
            </div>
            {form.procedures.filter(p => p.cpt_code === null && p.icd10_pcs_code !== null && p.icd10_pcs_code !== undefined).length === 0 && (
              <p style={s.hint}>No ICD-10 PCS procedures added.</p>
            )}
            {form.procedures.map((proc, i) => {
              if (!(proc.cpt_code === null && proc.icd10_pcs_code !== null && proc.icd10_pcs_code !== undefined)) return null;
              return (
                <div key={`pcs-${i}`} style={s.row}>
                  <input value={proc.icd10_pcs_code} onChange={e => updateProcedure(i, 'icd10_pcs_code', e.target.value)} placeholder="e.g. 0FT44ZZ" style={s.inputSmall} />
                  <input required value={proc.description} onChange={e => updateProcedure(i, 'description', e.target.value)} placeholder="Procedure name" style={{ ...s.inputSmall, flex: 2 }} />
                  <input required type="number" min="0" value={proc.amount} onChange={e => updateProcedure(i, 'amount', e.target.value)} placeholder="0" style={s.inputSmall} />
                  <input type="date" value={proc.date || ''} onChange={e => updateProcedure(i, 'date', e.target.value)} style={s.inputSmall} />
                  <button type="button" onClick={() => removeProcedure(i)} style={s.btnDanger}>✕</button>
                </div>
              );
            })}
            <button type="button" onClick={() => addProcedureOfType('pcs')} style={s.btnAdd}>+ Add ICD-10 PCS Procedure</button>
          </div>
        </fieldset>

        {/* Section 7: Estimated Cost */}
        <fieldset style={s.fieldset}>
          <legend style={s.legend}>7. Estimated Cost & Financial Details</legend>
          <div style={s.grid}>
            <F label="Room Rent (per day)">
              <input type="number" min="0" value={form.cost_breakdown.room_rent_per_day} onChange={e => uCost('room_rent_per_day', e.target.value)} style={s.input} />
            </F>
            <F label="Consultation Charges">
              <input type="number" min="0" value={form.cost_breakdown.consultation_charges} onChange={e => uCost('consultation_charges', e.target.value)} style={s.input} />
            </F>
            <F label="Investigation Charges">
              <input type="number" min="0" value={form.cost_breakdown.investigation_charges} onChange={e => uCost('investigation_charges', e.target.value)} style={s.input} />
            </F>
            <F label="Surgery / Procedure Charges">
              <input type="number" min="0" value={form.cost_breakdown.surgery_charges} onChange={e => uCost('surgery_charges', e.target.value)} style={s.input} />
            </F>
            <F label="ICU Charges">
              <input type="number" min="0" value={form.cost_breakdown.icu_charges} onChange={e => uCost('icu_charges', e.target.value)} style={s.input} />
            </F>
            <F label="Medicine & Consumables">
              <input type="number" min="0" value={form.cost_breakdown.medicine_consumables} onChange={e => uCost('medicine_consumables', e.target.value)} style={s.input} />
            </F>
            <F label="Implant / Stent Cost">
              <input type="number" min="0" value={form.cost_breakdown.implant_cost} onChange={e => uCost('implant_cost', e.target.value)} style={s.input} />
            </F>
            <F label="Other Charges">
              <input type="number" min="0" value={form.cost_breakdown.other_charges} onChange={e => uCost('other_charges', e.target.value)} style={s.input} />
            </F>
            <F label="Total Estimated Amount" required><Inp field="total_claimed_amount" required type="number" min="0" /></F>
          </div>
        </fieldset>

        {/* Section 8 & 9: Declarations */}
        <fieldset style={s.fieldset}>
          <legend style={s.legend}>8 & 9. Declarations</legend>
          <label style={s.checkRow}>
            <input type="checkbox" checked={form.doctor_declaration} onChange={e => u('doctor_declaration', e.target.checked)} />
            <span>Doctor Declaration — I certify that the proposed treatment is medically necessary.</span>
          </label>
          <label style={s.checkRow}>
            <input type="checkbox" checked={form.patient_declaration} onChange={e => u('patient_declaration', e.target.checked)} />
            <span>Patient Declaration — I consent to sharing medical information and undertake to pay non-covered / excess expenses.</span>
          </label>
        </fieldset>

        <button type="submit" disabled={loading} style={s.btnSubmit}>
          {loading ? 'Processing Claim...' : 'Submit Claim'}
        </button>
      </form>
    </div>
  );
}

const s = {
  container: { maxWidth: 960, margin: '0 auto', padding: 24, fontFamily: 'system-ui, sans-serif' },
  title: { textAlign: 'center', color: '#1a365d', marginBottom: 24 },
  fieldset: { border: '1px solid #e2e8f0', borderRadius: 8, padding: 16, marginBottom: 16 },
  legend: { fontWeight: 600, color: '#2d3748', padding: '0 8px', fontSize: 15 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 },
  field: { display: 'flex', flexDirection: 'column', gap: 4 },
  input: { padding: '8px 12px', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: 14 },
  textarea: { padding: '8px 12px', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: 14, minHeight: 60, resize: 'vertical' },
  inputSmall: { padding: '6px 10px', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: 13, flex: 1 },
  select: { padding: '6px 10px', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: 13 },
  row: { display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 },
  colHeader: { flex: 1, fontSize: 11, fontWeight: 600, color: '#718096', textTransform: 'uppercase', letterSpacing: '0.5px' },
  fileRow: { display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6, padding: '6px 0', borderBottom: '1px solid #edf2f7' },
  fileName: { flex: 1, fontSize: 13, color: '#4a5568' },
  hint: { fontSize: 13, color: '#718096', marginBottom: 8 },
  checkRow: { display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 10, fontSize: 14, cursor: 'pointer' },
  btnSmall: { padding: '4px 12px', background: '#3182ce', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12 },
  btnDanger: { padding: '4px 8px', background: '#e53e3e', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12 },
  btnAdd: { padding: '6px 16px', background: '#edf2f7', color: '#2d3748', border: '1px solid #cbd5e0', borderRadius: 6, cursor: 'pointer', fontSize: 13, marginTop: 4 },
  btnSubmit: { width: '100%', padding: '12px 24px', background: '#2b6cb0', color: '#fff', border: 'none', borderRadius: 8, fontSize: 16, fontWeight: 600, cursor: 'pointer', marginTop: 16 },
  error: { background: '#fed7d7', color: '#c53030', padding: 12, borderRadius: 8, marginBottom: 16, whiteSpace: 'pre-wrap', fontSize: 13 },
  success: { background: '#c6f6d5', color: '#276749', padding: 12, borderRadius: 8, marginBottom: 16, fontSize: 14 },
};
